// packages/web/src/services/teacherLearningApi.ts
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// 请求拦截器：添加 token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：统一错误处理
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token 过期，跳转到登录页
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface RecognizeExercisesRequest {
  imagePath: string;
}

export interface RecognizeExercisesResponse {
  success: boolean;
  data?: {
    exercises: any[];
    total_count: number;
  };
  warning?: {
    message: string;
    needsRecharge?: boolean;
    rechargeUrl?: string;
  };
  error?: {
    message: string;
    code?: string;
    needsRecharge?: boolean;
    rechargeUrl?: string;
  };
}

export interface GenerateRequirementsRequest {
  exercises: any[];
  materialText?: string;
  materialFileBase64?: string;
  materialFileName?: string;
  materialFileType?: string;
}

export interface GenerateKnowledgeRequest {
  exercises: any[];
  exerciseImagePath: string;
  exercisesNeedingAI?: string[]; // 需要AI重新生成知识点的习题ID列表
}

export interface AssignExercisesRequest {
  templateIds: string[];
  studentIds: string[];
}

export interface AssignExercisesAsHomeworkRequest {
  name: string;
  description?: string;
  dueDate: string;
  templateIds: string[];
  classIds: string[];
  studentIds: string[];
}

export const teacherLearningApi = {
  /**
   * 识别习题集
   */
  async recognizeExercises(data: RecognizeExercisesRequest): Promise<RecognizeExercisesResponse> {
    const response = await apiClient.post('/teach/exercises/recognize', data);
    return response.data;
  },

  /**
   * 生成教学要求
   */
  async generateRequirements(data: GenerateRequirementsRequest): Promise<any> {
    const response = await apiClient.post('/teach/exercises/generate-requirements', data);
    return response.data;
  },

  /**
   * 生成知识点并保存
   */
  async generateKnowledge(data: GenerateKnowledgeRequest): Promise<any> {
    const response = await apiClient.post('/teach/exercises/generate-knowledge', data);
    return response.data;
  },

  /**
   * 布置作业
   */
  async assignExercises(data: AssignExercisesRequest): Promise<any> {
    const response = await apiClient.post('/teach/exercises/assign', data);
    return response.data;
  },

  /**
   * 布置作业（创建作业记录）
   */
  async assignExercisesAsHomework(data: AssignExercisesAsHomeworkRequest): Promise<any> {
    const response = await apiClient.post('/teach/exercises/assign-as-homework', data);
    return response.data;
  },

  /**
   * 获取历史教案
   */
  async getTeachingMaterialsHistory(limit: number = 10): Promise<any> {
    const response = await apiClient.get(`/teach/teaching-materials/history?limit=${limit}`);
    return response.data;
  },

  /**
   * 获取教案内容
   */
  async getTeachingMaterialContent(materialId: string): Promise<any> {
    const response = await apiClient.get(`/teach/teaching-materials/${materialId}/content`);
    return response.data;
  },

  /**
   * 更新教案使用时间
   */
  async updateTeachingMaterialUsage(materialId: string): Promise<any> {
    const response = await apiClient.post(`/teach/teaching-materials/${materialId}/use`);
    return response.data;
  },
};
